export type PostHogErrorProperty = string | number | boolean | null;

const MAX_CONTEXT_VALUE_LENGTH = 160;
const MAX_DIAGNOSTIC_LENGTH = 2_000;

function boundedString(value: unknown, maxLength = MAX_CONTEXT_VALUE_LENGTH): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.replace(/[\u0000-\u001f\u007f]/g, " ").trim();
  return normalized ? normalized.slice(0, maxLength) : undefined;
}

function buildInfo(): {
  commit: string;
  branch: string;
  version: string;
  buildNumber: number;
} {
  try {
    if (typeof __BUILD_INFO__ !== "undefined" && __BUILD_INFO__) {
      return {
        commit: boundedString(__BUILD_INFO__.commit) ?? "unknown",
        branch: boundedString(__BUILD_INFO__.branch) ?? "unknown",
        version: boundedString(__BUILD_INFO__.version) ?? "unknown",
        buildNumber: Number.isFinite(__BUILD_INFO__.buildNumber) ? __BUILD_INFO__.buildNumber : 0,
      };
    }
  } catch {
    // Build metadata is diagnostic context only. Never block app startup.
  }

  return { commit: "unknown", branch: "unknown", version: "unknown", buildNumber: 0 };
}

function currentRoute(): string {
  if (typeof window === "undefined") return "unknown";
  try {
    return boundedString(window.location.pathname, MAX_CONTEXT_VALUE_LENGTH) ?? "unknown";
  } catch {
    return "unknown";
  }
}

function defaultRuntime(): string {
  if (typeof window === "undefined") return "unknown";
  try {
    return window.location.protocol === "file:" ? "desktop" : "web";
  } catch {
    return "unknown";
  }
}

/**
 * Context shared by automatic and manual exception events.
 *
 * This intentionally contains only release and navigation metadata. Prompts,
 * response text, terminal output, credentials, and query strings stay out of
 * the event properties.
 */
export function getPostHogErrorContext(runtime = defaultRuntime()): Record<string, PostHogErrorProperty> {
  const info = buildInfo();
  return {
    app_release: info.commit,
    app_branch: info.branch,
    app_version: info.version,
    app_build: info.buildNumber,
    app_route: currentRoute(),
    app_runtime: boundedString(runtime) ?? "unknown",
  };
}

/** Keep optional diagnostic properties bounded and free of control characters. */
export function sanitizeDiagnostic(value: unknown, maxLength = MAX_DIAGNOSTIC_LENGTH): string | undefined {
  const diagnostic = boundedString(value, maxLength);
  if (!diagnostic) return undefined;

  // Query strings frequently contain one-time auth codes or provider tokens.
  // Keep the URL path for diagnosis, but never forward the query/hash.
  return diagnostic.replace(/\bhttps?:\/\/[^\s"'<>]+/gi, (url) => {
    const queryStart = url.search(/[?#]/);
    return queryStart >= 0 ? url.slice(0, queryStart) : url;
  });
}
